import { createElement, type ComponentType } from "react";
import type { LucideIcon } from "lucide-react";
import {
  BadgeCheck,
  Building2,
  CalendarPlus,
  ClipboardPlus,
  Clock,
  FileSearch,
  Files,
  GitBranch,
  Layers,
  Link2,
  History,
  Mail,
  Megaphone,
  MessageSquare,
  Send,
  ShieldCheck,
  Smartphone,
  Star,
  UserPlus,
} from "lucide-react";
import {
  ALL_PLATFORM_CATALOG,
  type PreCredentialPlatformCatalogItem,
} from "../../constants/preCredentialPlatformCatalog";
import type {
  AskMissingConfig,
  AutomationKind,
  AutomationLane,
  AutomationNode,
  AutomationScope,
  CheckFilesConfig,
  ConversationConfig,
  FillFormConfig,
  AutomationFamily,
  DelayConfig,
  HospitalAutomation,
  NotifyAgenciesConfig,
  NotifyEmailConfig,
  NotifySmsConfig,
  ProposeGroupConfig,
  ReleaseAudience,
  ReleaseStage,
  RequestFilesConfig,
  ReleaseStagesConfig,
  ShiftStartsWithinConfig,
} from "./types";

export const AUTOMATION_LANES: {
  id: AutomationLane;
  title: string;
  subtitle: string;
  required: boolean;
}[] = [
  { id: "listen", title: "Listen", subtitle: "Starts the flow", required: true },
  { id: "collect", title: "Collect", subtitle: "Messages, files, waits", required: false },
  { id: "condition", title: "Conditions", subtitle: "Yes / no branches", required: false },
  { id: "decide", title: "Actions", subtitle: "What to do", required: false },
  { id: "notify", title: "Notify", subtitle: "Email and SMS", required: false },
];

export const AUTOMATION_FAMILIES: {
  id: AutomationFamily;
  title: string;
}[] = [
  { id: "application", title: "Application" },
  { id: "post_shift", title: "Post shift" },
];

export type BranchPort = { id: string; label: string };

export function StatDoctorAppIcon({ className }: { className?: string }) {
  return createElement(
    "span",
    {
      className: `inline-flex items-center justify-center overflow-hidden rounded-[22%] bg-white ${className || ""}`,
    },
    createElement("img", {
      src: "/brand/statdoctor-mark.png",
      alt: "StatDoctor",
      className: "h-full w-full object-contain",
    }),
  );
}

export type AutomationKindMeta = {
  kind: AutomationKind;
  lane: AutomationLane;
  family?: AutomationFamily;
  label: string;
  description: string;
  icon: LucideIcon | ComponentType<{ className?: string }>;
  hidden?: boolean;
  nestUnder?: AutomationKind;
  outputs: BranchPort[];
};

export const DEFAULT_RELEASE_STAGES: ReleaseStage[] = [
  { audience: "internal", days: 10 },
  { audience: "precredential", days: 10 },
  { audience: "public", days: 0 },
];

export const RELEASE_AUDIENCE_OPTIONS: {
  id: ReleaseAudience;
  label: string;
  description: string;
}[] = [
  {
    id: "internal",
    label: "Internal bank",
    description: "Doctors on this hospital’s internal bank",
  },
  {
    id: "precredential",
    label: "Locum Bank",
    description: "Doctors on the locum / pre-credential bank",
  },
  {
    id: "favorite",
    label: "Favorites",
    description: "Doctors on the hospital favorites list",
  },
  {
    id: "worked_before",
    label: "Worked here before",
    description: "Doctors who completed a shift at this hospital",
  },
  {
    id: "public",
    label: "StatDoctor",
    description: "Open on the StatDoctor app",
  },
];

export function audienceLabel(audience: ReleaseAudience) {
  return (
    RELEASE_AUDIENCE_OPTIONS.find((item) => item.id === audience)?.label ||
    audience
  );
}

export const AUTOMATION_KINDS: AutomationKindMeta[] = [
  {
    kind: "listen.shift_applied",
    lane: "listen",
    family: "application",
    label: "Doctor applied for a shift",
    description: "Starts when a doctor applies.",
    icon: ClipboardPlus,
    outputs: [{ id: "out", label: "Then" }],
  },
  {
    kind: "listen.precredential_applied",
    lane: "listen",
    family: "application",
    label: "Doctor applied for pre-credential",
    description: "Starts when they ask to join the bank.",
    icon: UserPlus,
    outputs: [{ id: "out", label: "Then" }],
  },
  {
    kind: "listen.shift_posted",
    lane: "listen",
    family: "post_shift",
    label: "Shift posted",
    description: "Starts whenever this hospital posts a solo shift or a group.",
    icon: CalendarPlus,
    outputs: [{ id: "out", label: "Then" }],
  },
  {
    kind: "collect.conversation",
    lane: "collect",
    family: "application",
    label: "Message in conversation",
    description: "Post in the hospital–doctor chat, not SMS.",
    icon: MessageSquare,
    outputs: [{ id: "out", label: "Then" }],
  },
  {
    kind: "collect.send_sms",
    lane: "collect",
    family: "application",
    label: "Message in conversation",
    description: "Legacy alias.",
    icon: MessageSquare,
    hidden: true,
    outputs: [{ id: "out", label: "Then" }],
  },
  {
    kind: "collect.request_files",
    lane: "collect",
    family: "application",
    label: "Request files",
    description: "Ask for specific documents.",
    icon: Files,
    outputs: [{ id: "out", label: "Then" }],
  },
  {
    kind: "collect.fill_form",
    lane: "collect",
    family: "application",
    label: "Fill a form from a guide",
    description: "Send a document-guide link to complete.",
    icon: Link2,
    outputs: [{ id: "out", label: "Then" }],
  },
  {
    kind: "collect.check_files",
    lane: "collect",
    family: "application",
    label: "Check profile files",
    description: "Split: files present, or missing.",
    icon: FileSearch,
    outputs: [
      { id: "present", label: "All present" },
      { id: "missing", label: "Missing" },
    ],
  },
  {
    kind: "collect.ask_missing",
    lane: "collect",
    family: "application",
    label: "Ask for missing documents",
    description: "Message them in conversation to add what’s missing.",
    icon: MessageSquare,
    hidden: true,
    outputs: [{ id: "out", label: "Then" }],
  },
  {
    kind: "collect.delay",
    lane: "collect",
    family: "post_shift",
    label: "Wait days",
    description: "Wait this many days after the previous step, then continue.",
    icon: Clock,
    outputs: [{ id: "out", label: "Then" }],
  },
  {
    kind: "condition.precredentialed",
    lane: "condition",
    family: "application",
    label: "If pre-credentialed",
    description: "Yes if they are already in the pre-credential bank.",
    icon: ShieldCheck,
    outputs: [
      { id: "yes", label: "Yes" },
      { id: "no", label: "No" },
    ],
  },
  {
    kind: "condition.internal",
    lane: "condition",
    family: "application",
    label: "If internal",
    description: "Yes if they are on this hospital’s internal bank.",
    icon: Building2,
    outputs: [
      { id: "yes", label: "Yes" },
      { id: "no", label: "No" },
    ],
  },
  {
    kind: "condition.meets_requirement",
    lane: "condition",
    family: "application",
    label: "If specialty and skill match",
    description: "Yes if they meet the Listen specialty and skill, even if they are not in a bank.",
    icon: GitBranch,
    outputs: [
      { id: "yes", label: "Yes" },
      { id: "no", label: "No" },
    ],
  },
  {
    kind: "decide.auto_approve",
    lane: "decide",
    family: "application",
    label: "Auto-approve",
    description: "Approve the application when this step is reached.",
    icon: BadgeCheck,
    outputs: [{ id: "out", label: "Then" }],
  },
  {
    kind: "decide.propose_group",
    lane: "decide",
    family: "application",
    label: "Propose other shifts in the group",
    description: "Offer the other dates in the same shift group.",
    icon: Layers,
    outputs: [{ id: "out", label: "Then" }],
  },
  {
    kind: "condition.shift_starts_within",
    lane: "condition",
    family: "post_shift",
    label: "If it starts in less than",
    description: "Yes if the posted work starts sooner than the days you set. Solo uses that date. A group uses the first date.",
    icon: Clock,
    outputs: [
      { id: "yes", label: "Yes" },
      { id: "no", label: "No" },
    ],
  },
  {
    kind: "decide.release_stages",
    lane: "decide",
    family: "post_shift",
    label: "Staged release",
    description: "Legacy bundled release.",
    icon: Send,
    hidden: true,
    outputs: [{ id: "out", label: "Then" }],
  },
  {
    kind: "decide.post_internal",
    lane: "decide",
    family: "post_shift",
    label: "Post for internal bank",
    description: "Open the shift to the hospital’s internal bank.",
    icon: Building2,
    outputs: [{ id: "out", label: "Then" }],
  },
  {
    kind: "decide.post_precredential",
    lane: "decide",
    family: "post_shift",
    label: "Post for Locum Bank",
    description: "Open the shift to the locum bank.",
    icon: ShieldCheck,
    outputs: [{ id: "out", label: "Then" }],
  },
  {
    kind: "decide.post_favorite",
    lane: "decide",
    family: "post_shift",
    label: "Post for favorites",
    description: "Open the shift to favorite doctors.",
    icon: Star,
    outputs: [{ id: "out", label: "Then" }],
  },
  {
    kind: "decide.post_worked_before",
    lane: "decide",
    family: "post_shift",
    label: "Post for worked here before",
    description: "Open the shift to doctors who already worked here.",
    icon: History,
    outputs: [{ id: "out", label: "Then" }],
  },
  {
    kind: "decide.post_public",
    lane: "decide",
    family: "post_shift",
    label: "Post to StatDoctor publicly",
    description: "Post the shift on the StatDoctor app.",
    icon: StatDoctorAppIcon,
    outputs: [{ id: "out", label: "Then" }],
  },
  {
    kind: "notify.email",
    lane: "notify",
    label: "Notify by email",
    description: "Email the hospital team. On applications you can also email the doctor.",
    icon: Mail,
    outputs: [{ id: "out", label: "Then" }],
  },
  {
    kind: "notify.sms",
    lane: "notify",
    family: "application",
    hidden: true,
    label: "Notify by SMS",
    description: "Not used.",
    icon: Smartphone,
    outputs: [{ id: "out", label: "Then" }],
  },
  {
    kind: "notify.agencies",
    lane: "notify",
    family: "post_shift",
    label: "Broadcast to locum agencies",
    description: "Email locum agencies that locum doctors are needed. Separate from posting on StatDoctor.",
    icon: Megaphone,
    outputs: [{ id: "out", label: "Then" }],
  },
];

export function createNodeId() {
  return `n_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function createEdgeId() {
  return `e_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function emptyScope(): AutomationScope {
  return { specialtyIds: [] };
}

export function createAutomationId() {
  return `a_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function metaForKind(kind: AutomationKind) {
  return (
    AUTOMATION_KINDS.find((item) => item.kind === kind) ||
    AUTOMATION_KINDS.find((item) => item.kind === "collect.conversation")!
  );
}

export function outputsForKind(kind: AutomationKind): BranchPort[] {
  return metaForKind(kind).outputs;
}

export function defaultConfig(kind: AutomationKind) {
  switch (kind) {
    case "listen.shift_applied":
    case "listen.precredential_applied":
    case "listen.shift_posted":
    case "condition.precredentialed":
    case "condition.internal":
    case "condition.meets_requirement":
    case "decide.auto_approve":
    case "decide.post_internal":
    case "decide.post_precredential":
    case "decide.post_favorite":
    case "decide.post_worked_before":
    case "decide.post_public":
      return {};
    case "collect.conversation":
    case "collect.send_sms":
      return {
        message:
          "Hi, we received your application. Please reply in this conversation if you need anything.",
        optional: false,
      } as ConversationConfig;
    case "collect.request_files":
      return { files: [], optional: false } as RequestFilesConfig;
    case "collect.fill_form":
      return {
        guides: [],
        requireVerified: true,
        optional: false,
      } as FillFormConfig;
    case "collect.check_files":
      return { files: [], optional: false } as CheckFilesConfig;
    case "collect.ask_missing":
      return {
        message:
          "Some required documents are missing from your profile. Please add them here or on your StatDoctor profile.",
        optional: false,
      } as AskMissingConfig;
    case "collect.delay":
      return { days: 10, optional: false } as DelayConfig;
    case "decide.propose_group":
      return {
        includeOtherDates: true,
        message:
          "We also have other dates in this shift group. Reply here if you want us to propose those shifts.",
        optional: false,
      } as ProposeGroupConfig;
    case "condition.shift_starts_within":
      return { days: 7, scope: "group_first" } as ShiftStartsWithinConfig;
    case "decide.release_stages":
      return {
        stages: DEFAULT_RELEASE_STAGES.map((stage) => ({ ...stage })),
        optional: false,
      } as ReleaseStagesConfig;
    case "notify.agencies":
      return {
        emails: [],
        subject: "New locum doctors needed",
        body: "New locum doctors needed.\n\n{{openDates}}\nSpecialty: {{specialty}}\nSkill level: {{skillLevel}}",
        optional: false,
      } as NotifyAgenciesConfig;
    case "notify.email":
      return {
        hospital: true,
        doctor: false,
        subject: "A doctor applied",
        body: "A doctor just applied. Open Hire or Bank to review the application.",
        optional: false,
      } as NotifyEmailConfig;
    case "notify.sms":
      return {
        hospital: false,
        doctor: true,
        message: "A doctor applied. Open StatHospital to review.",
        optional: false,
      } as NotifySmsConfig;
  }
}

export function createNode(kind: AutomationKind): AutomationNode {
  const resolved = kind === "collect.send_sms" ? "collect.conversation" : kind;
  const meta = metaForKind(resolved);
  return {
    id: createNodeId(),
    kind: resolved,
    lane: meta.lane,
    config: defaultConfig(resolved),
    position: { x: 48, y: 48 },
    scope: emptyScope(),
  };
}

export function createEmptyAutomation(hospitalId: string): HospitalAutomation {
  const now = new Date().toISOString();
  return {
    id: createAutomationId(),
    hospitalId,
    name: "Untitled automation",
    enabled: false,
    nodes: [],
    edges: [],
    createdAt: now,
    updatedAt: now,
  };
}

export function suggestedName(kind: AutomationKind) {
  if (kind === "listen.shift_applied") return "When a doctor applies for a shift";
  if (kind === "listen.precredential_applied") {
    return "When a doctor applies for pre-credential";
  }
  if (kind === "listen.shift_posted") return "When a shift is posted";
  return "Untitled automation";
}

export function notifyTargetLabel(hospital?: boolean, doctor?: boolean) {
  if (hospital && doctor) return "Hospital and doctor";
  if (doctor) return "Doctor";
  if (hospital) return "Hospital";
  return "Pick hospital or doctor";
}

export function summarizeStages(stages?: ReleaseStage[]) {
  if (!stages?.length) return "Add release stages";
  return stages
    .map((stage) => {
      const label = audienceLabel(stage.audience);
      return stage.audience === "public" && !stage.days
        ? `then ${label}`
        : `${label} ${stage.days}d`;
    })
    .join(" → ");
}

export const LANE_TONE: Record<
  AutomationLane,
  { accent: string; soft: string; ring: string; text: string }
> = {
  listen: {
    accent: "bg-[#023EFF]",
    soft: "bg-[#023EFF]/10",
    ring: "ring-[#023EFF]/30",
    text: "text-[#023EFF]",
  },
  collect: {
    accent: "bg-teal-600",
    soft: "bg-teal-50",
    ring: "ring-teal-200",
    text: "text-teal-700",
  },
  condition: {
    accent: "bg-rose-500",
    soft: "bg-rose-50",
    ring: "ring-rose-200",
    text: "text-rose-700",
  },
  decide: {
    accent: "bg-amber-500",
    soft: "bg-amber-50",
    ring: "ring-amber-200",
    text: "text-amber-700",
  },
  notify: {
    accent: "bg-violet-600",
    soft: "bg-violet-50",
    ring: "ring-violet-200",
    text: "text-violet-700",
  },
};

export function profileFileCatalog(): PreCredentialPlatformCatalogItem[] {
  return ALL_PLATFORM_CATALOG.filter((item) => !item.generalSetting);
}

export function requestableFiles() {
  return profileFileCatalog().filter(
    (item) => item.kind === "document" || item.kind === "identity_document",
  );
}

export function checkableFiles() {
  return profileFileCatalog();
}

export function hasListen(nodes: AutomationNode[]) {
  return nodes.some((node) => node.lane === "listen");
}

export function familyTitle(family?: AutomationFamily) {
  return AUTOMATION_FAMILIES.find((item) => item.id === family)?.title || "";
}

export function familyForKind(kind: AutomationKind): AutomationFamily | undefined {
  return metaForKind(kind).family;
}

export function listenFamilies(nodes: AutomationNode[]): AutomationFamily[] {
  const found = new Set<AutomationFamily>();
  nodes.forEach((node) => {
    if (node.lane !== "listen") return;
    const family = familyForKind(node.kind);
    if (family) found.add(family);
  });
  return AUTOMATION_FAMILIES.map((item) => item.id).filter((id) => found.has(id));
}

export function kindMatchesPath(
  kind: AutomationKind,
  path: AutomationFamily | "both" | null,
) {
  if (!path) return metaForKind(kind).lane === "listen";
  if (path === "both") return true;
  const family = familyForKind(kind);
  if (!family) return true;
  return family === path;
}

export function visibleKindsForLane(lane: AutomationLane) {
  return AUTOMATION_KINDS.filter(
    (item) => item.lane === lane && !item.hidden && !item.nestUnder,
  );
}

export function nestedKinds(parent: AutomationKind) {
  return AUTOMATION_KINDS.filter(
    (item) => item.nestUnder === parent && !item.hidden,
  );
}

export function familiesForLane(lane: AutomationLane): AutomationFamily[] {
  const used = new Set(
    visibleKindsForLane(lane)
      .map((item) => item.family)
      .filter((item): item is AutomationFamily => Boolean(item)),
  );
  return AUTOMATION_FAMILIES.map((item) => item.id).filter((id) => used.has(id));
}

export function summarizeScope(scope?: AutomationScope) {
  const specialties = scope?.specialtyIds?.length || 0;
  const skill = scope?.minimumSkillId ? "skill filter" : null;
  if (!specialties && !skill) return "Any specialty · any skill";
  const parts = [
    specialties ? `${specialties} specialt${specialties === 1 ? "y" : "ies"}` : "Any specialty",
    skill || "any skill",
  ];
  return parts.join(" · ");
}

export function summarizeNode(node: AutomationNode): string {
  const config = node.config as Record<string, any>;
  switch (node.kind) {
    case "listen.shift_applied":
    case "listen.precredential_applied":
    case "listen.shift_posted":
      return summarizeScope(node.scope);
    case "collect.conversation":
    case "collect.send_sms":
      return "Hospital–doctor conversation";
    case "collect.request_files": {
      const n = config.files?.length || 0;
      return n ? `${n} file${n === 1 ? "" : "s"}` : "No files selected";
    }
    case "collect.fill_form": {
      const n = Array.isArray(config.guides) ? config.guides.length : config.guideId ? 1 : 0;
      return n ? `${n} guide${n === 1 ? "" : "s"}` : "No guide selected";
    }
    case "collect.check_files": {
      const n = config.files?.length || 0;
      return n ? `Check ${n} · then branch` : "Pick files to check";
    }
    case "collect.ask_missing":
      return "Ask in conversation";
    case "condition.precredentialed":
      return "Yes / no";
    case "condition.internal":
      return "Yes / no";
    case "condition.meets_requirement":
      return "Uses Listen specialty and skill";
    case "condition.shift_starts_within":
      return `First date < ${Number(config.days) || 0}d`;
    case "decide.auto_approve":
      return "Approve when reached";
    case "collect.delay":
      return `Wait ${Number(config.days) || 0} day${Number(config.days) === 1 ? "" : "s"}`;
    case "decide.propose_group":
      return config.includeOtherDates === false
        ? "Off"
        : "Other dates in the group";
    case "decide.release_stages":
      return summarizeStages(config.stages);
    case "decide.post_internal":
      return "Internal bank";
    case "decide.post_precredential":
      return "Locum Bank";
    case "decide.post_favorite":
      return "Favorite doctors";
    case "decide.post_worked_before":
      return "Worked here before";
    case "decide.post_public":
      return "StatDoctor app";
    case "notify.email":
    case "notify.sms":
      return notifyTargetLabel(config.hospital, config.doctor);
    case "notify.agencies": {
      const n = Array.isArray(config.emails) ? config.emails.length : 0;
      return n ? `${n} agenc${n === 1 ? "y" : "ies"}` : "Add agency emails";
    }
    default:
      return "";
  }
}

export function summarizeFlow(automation: HospitalAutomation) {
  if (!automation.nodes.length) return "No steps yet";
  const listens = automation.nodes.filter((node) => node.lane === "listen").length;
  return `${automation.nodes.length} steps${listens ? ` · ${listens} listen` : ""}`;
}

export function validateAutomation(automation: HospitalAutomation): string[] {
  const errors: string[] = [];
  if (!automation.name.trim()) errors.push("Give this automation a name.");
  if (!hasListen(automation.nodes)) {
    errors.push("Add at least one Listen. That is what starts the automation.");
  }

  automation.nodes.forEach((node) => {
    const label = metaForKind(node.kind).label;
    if (node.kind === "collect.conversation" || node.kind === "collect.send_sms" || node.kind === "collect.ask_missing") {
      const cfg = node.config as ConversationConfig | AskMissingConfig;
      if (!cfg.message?.trim()) errors.push(`${label}: add a message.`);
    }
    if (node.kind === "collect.request_files") {
      const cfg = node.config as RequestFilesConfig;
      if (!cfg.files?.length) errors.push(`${label}: pick at least one file.`);
    }
    if (node.kind === "collect.fill_form") {
      const cfg = node.config as FillFormConfig;
      if (!cfg.guides?.length && !cfg.guideId) {
        errors.push(`${label}: choose at least one document guide.`);
      }
    }
    if (node.kind === "collect.check_files") {
      const cfg = node.config as CheckFilesConfig;
      if (!cfg.files?.length) errors.push(`${label}: pick files to check.`);
    }
    if (node.kind === "notify.email") {
      const cfg = node.config as NotifyEmailConfig;
      if (!cfg.hospital && !cfg.doctor) {
        errors.push(`${label}: pick Notify hospital and/or Notify doctor.`);
      }
      if (!cfg.subject?.trim()) errors.push(`${label}: add an email subject.`);
    }
    if (node.kind === "notify.sms") {
      const cfg = node.config as NotifySmsConfig;
      if (!cfg.hospital && !cfg.doctor) {
        errors.push(`${label}: pick Notify hospital and/or Notify doctor.`);
      }
      if (!cfg.message?.trim()) errors.push(`${label}: add an SMS message.`);
    }
    if (node.kind === "collect.delay") {
      const cfg = node.config as DelayConfig;
      if (!cfg.days || cfg.days < 1) errors.push(`${label}: set at least 1 day.`);
    }
    if (node.kind === "decide.propose_group") {
      const cfg = node.config as ProposeGroupConfig;
      if (!cfg.message?.trim()) {
        errors.push(`${label}: add the proposal message.`);
      }
    }
    if (node.kind === "decide.release_stages") {
      const cfg = node.config as ReleaseStagesConfig;
      if (!cfg.stages?.length) errors.push(`${label}: add at least one stage.`);
    }
    if (node.kind === "condition.shift_starts_within") {
      const cfg = node.config as ShiftStartsWithinConfig;
      if (!cfg.days || cfg.days < 1) errors.push(`${label}: set at least 1 day.`);
    }
    if (node.kind === "notify.agencies") {
      const cfg = node.config as NotifyAgenciesConfig;
      if (!cfg.emails?.filter((email) => email.includes("@")).length) {
        errors.push(`${label}: add at least one agency email.`);
      }
      if (!cfg.subject?.trim()) errors.push(`${label}: add an email subject.`);
    }
  });

  return errors;
}
